<template>
<div class="container-fluid">

	<freemium-locked-alert v-if="$store.company.is_freemium == 1"></freemium-locked-alert>

	<onboarding-card title="Set up expense groups & codes" name="Expense Codes" setting-key="expenseCodes">
		<p>Setting up expense codes to reflect your accounting software can help you better track and organized expenses. We have added some examples below.</p>
	</onboarding-card>

	<div class="row">
		<div class="col-md-6">
			<widget title="Expense Groups" class="row-padding">

				<form v-on:submit.prevent="addGroup">
					<div class="d-flex align-items-center">
						<label class="mr-2 mb-0 font-weight-bold">Group Name</label>
						<field :params="{placeholder:'Group Name'}" v-model="group.group_name"></field>
						<button 
							type="submit" 
							class="btn btn-success">
							<i class="fa fa-save"></i>
							{{ groupUpdating ? 'Update' : 'Add' }}
						</button>
					</div>
				</form>

				<table-list
					:records="$store.expenseGroups"
					:fields="groupFields"
					record-name="Group"
					:hide-record-count="true"
					:has-delete="true"
					:has-edit="true"
					delete-endpoint="/ExpenseGroup/delete"
					v-on:edit="editGroup"
					v-on:modelChange="fetchModel('ExpenseGroup')">
				</table-list>
			</widget>
			
		</div>
		<div class="col-md-6">
			<widget title="Expense Codes" class="row-padding">

				<form v-on:submit.prevent="addCode" class="mb-2">

					<field label="Code" col-class="col-md-4" :params="{placeholder:'Code Name'}" v-model="code.code"></field>

					<field label="Description" col-class="col-md-4" :params="{placeholder:'Description'}" v-model="code.description"></field>

					<field label="Group" col-class="col-md-4" :params="{type:'select',options:expenseGroupOptions}" v-model="code.group_id"></field>

					<field v-if="qboIsActive"
						label="Quickbooks Account"
						col-class="col-md-4"
						:params="{type:'select',options: qboAccountOptions}"
						v-model="code.qbo_id">
						The Quickbooks Online Account this code maps to
					</field>

					<button 
						type="submit" 
						class="btn btn-success float-right">
						<i class="fa fa-save"></i>
						{{ codeUpdating ? 'Update' : 'Add' }}
					</button>

					<div style="clear: both;"></div>

				</form>

				<table-list
					:records="mutatedCodes"
					:fields="codeFields"
					record-name="Code"
					:hide-record-count="true"
					:has-delete="true"
					:has-edit="true"
					delete-endpoint="/ExpenseCode/delete"
					v-on:edit="editCode"
					v-on:modelChange="fetchExpenseCodes">

					<button 
						v-if="qboIsActive"
						v-on:click="importQBO" 
						class="btn btn-light mr-2" 
						:disabled="importing">

						<i class="fa" :class="importing ? 'fa-spinner fa-spin' : 'fa-download'"></i>
						Import from QBO
					</button>

				</table-list>
			</widget>
			
		</div>
	</div>

</div>
</template>

<script>
import TableList from "@/components/TableList"
import Field from "@/components/Field"
import Widget from "@/components/Widget"
import OnboardingCard from "@/components/OnboardingCard"
import FreemiumLockedAlert from "@/components/FreemiumLockedAlert"

export default {
	name: 'ExpenseCodes',
	components: {
		TableList,
		Field,
		Widget,
		OnboardingCard,
		FreemiumLockedAlert
	},
	data() {
		return {
			groupFields: [
				{label: "Group Name",key:"group_name"}
			],
			group: {
				group_name: "",
			},
			code: {
				description: "",
				code: "",
				group_id: null,
				qbo_id: null
			},
			qboExpenseAccounts: [],
			qboConnection: null,
			codeUpdating: false,
			groupUpdating: false,
			importing: false
		}
	},
	watch: {
		qboIsActive() {
			if (this.qboIsActive) {
				this.fetchQboExpenseAccounts()
				this.fetchQboConnection()
			}
		}
	},
	computed: {
		codeFields() {
			let codeFields = [
				{label: "Group",key: "expenseGroup"},
				{label: "Code",key: "code"},
				{label: "Description",key: "description"}
			]

			if (this.qboIsActive)
				codeFields.push({label: "QBO",type:"boolean",key:"isQboCode"})

			return codeFields
		},
		qboAccountOptions() {
			return this.qboExpenseAccounts.map(account => {
				return {text: account.Name, value: account.Id}
			})
		},
		mutatedCodes() {
			let vm = this

			if (this.$store.expenseCodes)
				return this.$store.expenseCodes.map(code => {
					code.expenseGroupe = ""
					if ("ExpenseGroup" in code && code.ExpenseGroup)
						code.expenseGroup = code.ExpenseGroup.group_name

					code.isQboCode = null
					if (code.qbo_id > 0)
						code.isQboCode = true

					return code
				})
			else
				return []
		}
	},
	methods: {
		editCode(code) {
			this.code = {
				id: code.id,
				code: code.code,
				description: code.description,
				group_id: code.group_id,
				qbo_id: code.qbo_id
			}
			this.codeUpdating = true;
		},
		editGroup(group) {
			this.groupUpdating = true
			this.group = {
				group_name: group.group_name,
				id: group.id
			}
		},
		addCode() {
			let vm = this
			this.saveRecord("ExpenseCode",this.code,response => {
				vm.alertSaved("Expense Code added!")
				vm.code = {
					code: "",
					description: "",
					group_id: null,
					qbo_id: null
				}

				vm.codeUpdating = false
				vm.fetchExpenseCodes()
			})
		},
		addGroup() {
			
			let vm = this
			this.saveRecord("ExpenseGroup",this.group,response => {
				vm.alertSaved("Expense Group added!")
				vm.group = {
					group_name: ""
				}
				this.groupUpdating = false
				vm.fetchModel('ExpenseGroup')
			})
		},
		importQBO() {
			let vm = this

			this.spinner(true)
			this.importing = true

			this.getOrCreateAndReturnUncategorizedGroup().then(group_id => {

				let records = vm.qboExpenseAccounts
					.filter(account => !vm.$store.expenseCodes.find(code => code.qbo_id == account.Id))
					.map((account,index) => {

						return {
							code: index.pad(3),
							description: account.Name,
							group_id,
							qbo_id: account.Id
						}
					})

				let payload = {
					records: JSON.stringify(records)
				}

				vm.postData("ExpenseCode/save-batch",payload,true).then(response => {
					vm.spinner(false)
					vm.importing = false

					if (response.success) {
						vm.alertSuccess(response.data.records_saved+" expense codes imported from Quickbooks Online!")
						vm.fetchModel('ExpenseGroup')
						vm.fetchExpenseCodes()
					}
				});

			})
		},
		getOrCreateAndReturnUncategorizedGroup() {
			//returns group_id, either by retrieving the matching record or creating one
			let vm = this

			return new Promise((resolve,reject) => {

				//first check to see if there is an "Uncategorized" group
				let uncatGroup = this.$store.expenseGroups.find(group => group.group_name == 'Uncategorized')

				if (uncatGroup) {
					resolve(uncatGroup.id)
				}
				else {
					vm.postData("ExpenseGroup/save",{group_name: "Uncategorized"}).then(response => {
						if (response.success) 
							resolve(response.data.record_id)
						else
							reject(false)
					})
				}
			})
		},
		fetchQboExpenseAccounts() {
			let vm = this

			return new Promise((resolve,reject) => {
				vm.getJSON("QuickbooksConnection/get-resource",{resource:"Account",where:"Classification = 'Expense'"}).then(response => {
					if (response.success)
						vm.qboExpenseAccounts = response.data
				
					resolve(response)
				})
			})
		},
		fetchQboConnection() {
			let vm = this

			return new Promise((resolve,reject) => {
				vm.getJSON("QuickbooksConnection/get").then(response => {
					if (response.success && response.data.length > 0)
						vm.qboConnection = response.data[0]
				
					resolve(response)
				})
			})
		},
		fetchData() {
			let vm = this

			this.spinner(true)

			let promises = [
				this.fetchExpenseCodes(),
				this.fetchModel('ExpenseGroup')
			]

			if (this.qboIsActive) {
				promises.push(this.fetchQboConnection())
				promises.push(this.fetchQboExpenseAccounts())
			}

			Promise.all(promises).then(responses => {
				vm.spinner(false)
			})
		}
	},
	created() {
		let vm = this

		this.fetchData()
	},
}
</script>
